import React, { useState, useEffect, Suspense } from 'react';
import { SubmitHandler, Controller, useForm } from 'react-hook-form';
import { IUserData, IUserLoginReq } from '@models/interfaces/user.interface';

import { Input, Button, Checkbox, Link } from 'geniebook-ui/dist';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLoginMutation, useVerifyLogin } from '@hooks/user.hook';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import AUTH_API from '@services/auth.service';
import './LoginForm.css';
import {
  USER_TYPE_PARENT,
  USER_TYPE_STUDENT,
  USER_TYPE_STUDENT_LEGACY,
  USER_TYPE_TEACHER,
  USER_TYPE_TEACHER_LEGACY,
  USER_VN_STUDENT,
} from '@utils/constants/user.constants';
import { useAtom } from 'jotai';
import { activeSubjectExpiredAtom } from '@atom/activeSubjectStore';

export interface propsType {
  selectAccountState: (
    dataUserType: IUserData[],
    options: { remember_me: boolean },
  ) => void;
}

interface IError {
  error: boolean;
  error_code: string;
  error_msg: string;
}
//validation login
const spacesOnly = (string: string) => /^(\s+$)/g.test(string);
const schema = yup
  .object({
    username: yup
      .string()
      .required('Enter your username or email.')
      .test(
        'Enter your username or email.',
        'Enter your username or email.',
        value => !spacesOnly(value?.toString() ?? ''),
      ),
    password: yup
      .string()
      .required('Enter your password.')
      .test(
        'Enter your password.',
        'Enter your password.',
        value => !spacesOnly(value?.toString() ?? ''),
      ),
  })
  .required();

const initialValues: IUserLoginReq = {
  username: '',
  multiple_account: true,
  password: '',
  remember_me: false,
};
export default function LoginForm({ selectAccountState }: propsType) {
  const [loginError, setLoginError] = useState<IError>();

  const navigate = useNavigate();
  // Query mutation Login
  const queryLoginMutation = useLoginMutation();
  const queryVerifyLogin = useVerifyLogin();

  const [searchParams] = useSearchParams();

  // Handle form login
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IUserLoginReq>({
    defaultValues: initialValues,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const uid = Cookies.get('uid');
    if (uid !== undefined || uid === '') {
      const user_type = Cookies.get('user_type');
      if (
        user_type === USER_TYPE_STUDENT ||
        user_type === USER_TYPE_STUDENT_LEGACY
      ) {
        navigate(
          {
            pathname: '/mathematics/worksheets',
            search: searchParams.toString(),
          },
          { state: { from: 'login' } },
        );
      } else if (user_type === USER_TYPE_PARENT) {
        navigate('/parent');
      } else {
        navigate(
          {
            pathname: '/mathematics/worksheets',
            search: searchParams.toString(),
          },
          { state: { from: 'login' } },
        );
      }
    }
  }, [navigate]);

  const onSubmit: SubmitHandler<IUserLoginReq> = data => {
    queryLoginMutation.mutate(data, {
      onSuccess(data, variables) {
        if (!data.data.error) {
          setLoginError({ error: false, error_code: '', error_msg: '' });
          let dataLogin: IUserData[] = data.data?.data;
          // GBS-1625 Remove the option to login to the parent's account. For users with joint accounts, remove the option to login to the parent’s account
          // done by setting the dataLogin to only have student user to automatically do next process as 1 account instead of joint
          // if (dataLogin.length > 1) {
          //   for (let i = 0; i < dataLogin.length; i++) {
          //     const user = dataLogin[i];
          //     if (
          //       user.user_type.toString() == USER_TYPE_STUDENT ||
          //       user.user_type.toString() == USER_TYPE_STUDENT_LEGACY
          //     ) {
          //       dataLogin = [user];
          //       break;
          //     }
          //   }
          // }
          if (dataLogin.length > 1) {
            if (variables.remember_me) {
              selectAccountState(dataLogin, { remember_me: true });
            } else {
              selectAccountState(dataLogin, { remember_me: false });
            }
          } else {
            const user_type: string = String(dataLogin[0].user_type);
            if (
              user_type === USER_TYPE_TEACHER ||
              user_type === USER_TYPE_TEACHER_LEGACY
            ) {
              const LoginErrorTeacher: IError = {
                error: true,
                error_code: 'teacher',
                error_msg: 'Incorrect username/email or password. Try again.',
              };
              setLoginError(LoginErrorTeacher);
            } else {
              queryVerifyLogin.mutate(dataLogin[0].user_uid, {
                onSettled() {
                  const cookieDomain = window.location.hostname.replace(
                    /(.*\.)?([^.]*\..*)/,
                    '$2',
                  );
                  if (variables.remember_me) {
                    Cookies.set('uid', dataLogin[0].user_uid, {
                      path: '/',
                      domain: cookieDomain,
                    });
                    Cookies.set('user_type', String(dataLogin[0].user_type));
                    Cookies.set(
                      'is_vn_student',
                      String(dataLogin[0].country_code) === USER_VN_STUDENT
                        ? '1'
                        : '0',
                    );
                    if (dataLogin[0].last_login_date)
                      Cookies.set(
                        'user_last_login',
                        dataLogin[0].last_login_date,
                      );
                  } else {
                    Cookies.set('uid', dataLogin[0].user_uid, {
                      expires: 1,
                      path: '/',
                      domain: cookieDomain,
                    });
                    Cookies.set('user_type', String(dataLogin[0].user_type), {
                      expires: 1,
                    });
                    Cookies.set(
                      'is_vn_student',
                      String(dataLogin[0].country_code) === USER_VN_STUDENT
                        ? '1'
                        : '0',
                      {
                        expires: 1,
                      },
                    );
                    if (dataLogin[0].last_login_date)
                      Cookies.set(
                        'user_last_login',
                        dataLogin[0].last_login_date,
                        { expires: 1 },
                      );
                  }
                  //* set cookie for login integration
                  const domain = window.location.hostname.replace(
                    /(.*\.)?([^.]*\..*)/,
                    '$2',
                  );

                  //* convert milliseconds to expiry date for cookies
                  const expiryDate = new Date();
                  expiryDate.setTime((dataLogin[0].expired_at ?? 0) * 1000);

                  //* handle if access_token is null / undefined
                  Cookies.set(
                    'gbadmin_token',
                    dataLogin[0].access_token ?? '',
                    { expires: expiryDate, path: '/', domain: domain },
                  );

                  Cookies.set('is_login', '1');
                  AUTH_API.defaults.headers.uid = dataLogin[0].user_uid;

                  navigate('/mathematics/worksheets', {
                    state: { from: 'login' },
                  });
                },
              });
            }
          }
        } else {
          setLoginError(data.data);
        }
        queryLoginMutation.reset();
      },
    });
    setTimeout(() => {
      setValue('password', '');
    }, 1000);
  };

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isRemember, setIsRemember] = useState(false);

  const handleShowPassword = () => {
    setIsShowPassword(!isShowPassword);
  };

  const handleRememberCheck = () => {
    setValue('remember_me', !getValues('remember_me'));
    setIsRemember(!isRemember);
  };

  const [, setActiveSubjectExpired] = useAtom(activeSubjectExpiredAtom);
  useEffect(() => {
    setActiveSubjectExpired(null);
  }, []);

  return (
    <form className="px-10 max-sm:px-[24px]" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-4">
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <Input
              mode={'light'}
              variant={'outside'}
              {...field}
              placeholder="Enter your username or email"
              className="w-full [&>input]:!rounded-lg"
              label="Username / Email"
              error={
                errors.username
                  ? errors.username &&
                    queryLoginMutation.isError &&
                    queryLoginMutation.error instanceof Error
                    ? queryLoginMutation.error?.message
                    : loginError?.error
                    ? loginError?.error_msg ==
                        'Username not found. Try again.' ||
                      loginError?.error_msg == 'Email not found. Try again.'
                      ? 'Incorrect username/email or password. Try again.'
                      : loginError?.error_msg
                    : errors.username.message
                  : queryLoginMutation.isError &&
                    queryLoginMutation.error instanceof Error
                  ? queryLoginMutation.error?.message
                  : loginError?.error
                  ? loginError?.error_msg == 'Username not found. Try again.' ||
                    loginError?.error_msg == 'Email not found. Try again.'
                    ? 'Incorrect username/email or password. Try again.'
                    : loginError?.error_msg
                  : ''
              }
              disabled={queryLoginMutation.isLoading}
            />
          )}
        />
        {/*{errors.username?.type === 'required' && (*/}
        {/*  <p className="text-error-500">Enter your username or email</p>*/}
        {/*)}*/}
      </div>
      <div className="mb-4">
        <div className="relative float-right">
          <div className="absolute top-[36px] right-4">
            <div
              className={`${
                errors.password ? 'text-alert-error' : 'text-primary-apricot'
              } hover:text-primary-skyblue text-bold cursor-pointer`}
              onClick={() => handleShowPassword()}
            >
              {isShowPassword ? 'Hide' : 'Show'}
            </div>
          </div>
        </div>
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input
              mode={'light'}
              variant={'outside'}
              {...field}
              placeholder="Enter your password"
              className="w-full input-password [&>input]:!rounded-lg"
              label="Password"
              type={isShowPassword ? 'text' : 'password'}
              error={errors.password ? errors.password.message : ''}
              disabled={queryLoginMutation.isLoading}
            />
          )}
        />
      </div>
      <Checkbox
        className="cursor-pointer"
        variant={'light'}
        isChecked={isRemember}
        isValidation={false}
        onChange={handleRememberCheck}
        label={'Remember me'}
        disabled={queryLoginMutation.isLoading}
      />
      <Suspense>
        <button
          className="w-full"
          disabled={
            !(watch('username').length > 0 && watch('password').length > 0)
          }
        >
          <Button
            className="!max-w-none w-full mt-4"
            variant="primary"
            disabled={
              !(watch('username').length > 0 && watch('password').length > 0)
            }
          >
            {queryLoginMutation.isLoading ? 'Processing...' : 'Login'}
          </Button>
        </button>
      </Suspense>
      <div className="mt-7 flex justify-center">
        <div onClick={() => navigate('/forgot-password')}>
          <Link className="text-[16px] !font-bold cursor-pointer">
            Forgot your password?
          </Link>
        </div>
      </div>
    </form>
  );
}
